import clsx from "clsx";
import { Check, Clock, LayoutGrid } from "lucide-react";
import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useMemo, useRef, useState } from "react";

import type { App6EchelonCode, App6Entry, App6Palette as Palette, OdbSide } from "../../api/types";
import { Dialog } from "../../components/Dialog";
import { Badge, Button, SearchField, SectionLabel } from "../../components/ui";
import { useMediaQuery } from "../../components/useMediaQuery";
import { t } from "../../i18n/i18n";
import { bi, entriesFor, entryFor, recentKeys, rememberRecent, searchEntries } from "./app6";
import { parseApp6, shortApp6, sidcOf, withM2 } from "./sidc";
import { UnitSymbol } from "./UnitSymbol";

/** Palette des symboles terrestres OTAN (APP-6) : recherche sur les libellés français et anglais, les mots-clés et
 *  le code ; groupes à gauche (récemment utilisés en tête) ; tuiles dessinées pour l'unité en cours (camp,
 *  échelon, QG, force opérationnelle, numéros) ; aperçu à droite ; qualificatif (secteur 2) en puces sous la
 *  grille. Clavier : la frappe filtre, flèches dans la grille, Tab vers les puces, Entrée choisit, Échap ferme. */

const ALL = "\u0000tous";
const RECENT = "\u0000recents";

export interface PaletteUnit {
  side: OdbSide;
  echelon: App6EchelonCode;
  hq: boolean;
  tf: boolean;
  number: string | null;
  higher: string | null;
  fonction: string | null;
  app6: string | null;
}

/** Liste des missions d'un GTIA commandé au niveau régiment : celle de l'infanterie, sinon celle de la mêlée blindée
 *  (le moteur la donne aussi aux GTIA interarmes, d'appui et de soutien), comme la fiche de l'unité. */
function familyLabel(family: App6Entry["family"]): string {
  return family === "INF" ? t("infanterie") : t("blindées");
}

export function App6Palette({
  palette,
  unit,
  onChoose,
  onClose,
}: {
  palette: Palette;
  unit: PaletteUnit;
  onChoose: (entry: App6Entry, app6: string) => void;
  onClose: () => void;
}) {
  const entries = useMemo(() => entriesFor(palette, unit.side), [palette, unit.side]);
  const initial = entryFor(palette, unit.fonction, unit.app6);
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState<string>(ALL);
  const [chosen, setChosen] = useState<App6Entry | null>(initial);
  const initialM2 = parseApp6(unit.app6)?.m2 ?? "00";
  const [m2, setM2] = useState<string>(initial ? initialM2 : "00");
  const [recents] = useState(() => recentKeys());
  const searchRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const wide = useMediaQuery("(min-width: 1024px)");
  const roomy = useMediaQuery("(min-width: 768px)");

  const recentEntries = useMemo(
    () => recents.map((key) => entries.find((e) => e.key === key)).filter((e): e is App6Entry => Boolean(e)),
    [recents, entries],
  );
  const found = useMemo(() => searchEntries(entries, query), [entries, query]);
  // Recherche : toute la palette ; sinon le groupe choisi.
  const shown = useMemo(() => {
    if (query.trim()) return found;
    if (group === RECENT) return recentEntries;
    return group === ALL ? entries : entries.filter((e) => e.group === group);
  }, [query, found, group, entries, recentEntries]);
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of found) map.set(e.group, (map.get(e.group) ?? 0) + 1);
    return map;
  }, [found]);
  const qualifiers = palette.qualifiers.filter((q) => q.sides.includes(unit.side));

  // Code choisi : celui de l'entrée, avec le qualificatif retenu (une entrée prédéfinie arrive avec le sien).
  const app6 = chosen ? withM2(chosen.app6, m2) : null;
  const sidc = (code: string | null) => sidcOf({ side: unit.side, app6: code, echelon: unit.echelon, hq: unit.hq, tf: unit.tf });
  const pick = (entry: App6Entry) => {
    setChosen(entry);
    setM2(parseApp6(entry.app6)?.m2 ?? "00");
  };
  const confirm = (entry = chosen) => {
    if (!entry) return;
    const code = entry === chosen && app6 ? app6 : entry.app6;
    rememberRecent(entry.key);
    onChoose(entry, code);
  };

  // L'entrée choisie reste visible quand la liste change (recherche, groupe) ; absente de la nouvelle liste, la grille
  // revient en haut (sinon les premiers résultats d'une recherche resteraient cachés sous le défilement d'avant).
  useEffect(() => {
    const selected = gridRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (selected) selected.scrollIntoView?.({ block: "nearest" });
    else if (gridRef.current) gridRef.current.scrollTop = 0;
  }, [chosen, shown]);

  // Flèches dans la grille : gauche / droite à la tuile voisine, haut / bas à la tuile la plus proche de la rangée
  // voisine (la grille suit la largeur de la fenêtre) ; une lettre tapée revient à la recherche.
  const onGridKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const tiles = [...(gridRef.current?.querySelectorAll<HTMLButtonElement>("[data-tile]") ?? [])];
    const index = tiles.findIndex((tile) => tile === document.activeElement);
    if (e.key === "Enter" && index >= 0) {
      e.preventDefault();
      const entry = shown[index];
      if (entry) confirm(entry === chosen ? chosen : entry);
      return;
    }
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey && e.key !== " ") {
      searchRef.current?.focus();
      return;
    }
    const move = (target: number) => {
      const tile = tiles[target];
      if (!tile) return;
      e.preventDefault();
      tile.focus();
      const entry = shown[target];
      if (entry) pick(entry);
    };
    if (e.key === "ArrowRight") move(Math.min(index + 1, tiles.length - 1));
    else if (e.key === "ArrowLeft") move(Math.max(index - 1, 0));
    else if (e.key === "Home") move(0);
    else if (e.key === "End") move(tiles.length - 1);
    else if ((e.key === "ArrowDown" || e.key === "ArrowUp") && index >= 0) {
      const from = tiles[index]!.getBoundingClientRect();
      const down = e.key === "ArrowDown";
      let best = -1;
      let score = Infinity;
      tiles.forEach((tile, i) => {
        const rect = tile.getBoundingClientRect();
        const dy = down ? rect.top - from.top : from.top - rect.top;
        if (dy <= 4) return;
        const value = dy * 1000 + Math.abs(rect.left - from.left);
        if (value < score) {
          score = value;
          best = i;
        }
      });
      if (best >= 0) move(best);
    }
  };

  const groupButton = (key: string, label: string, count: number | null, icon?: typeof Clock) => {
    const Icon = icon;
    const active = !query.trim() && group === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => {
          setGroup(key);
          setQuery("");
        }}
        aria-pressed={active}
        className={clsx(
          "flex min-h-8 w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-ui transition-colors",
          active ? "bg-selected text-ink" : "text-ink-soft hover:bg-hover hover:text-ink",
        )}
      >
        {Icon && <Icon size={14} aria-hidden className="shrink-0 text-ink-muted" />}
        <span className="min-w-0 flex-1 leading-snug break-words">{label}</span>
        {count !== null && <Badge size="sm">{count}</Badge>}
      </button>
    );
  };

  const preview = chosen && (
    <div className="space-y-2">
      <div className="flex justify-center rounded-lg bg-canvas py-3">
        <UnitSymbol sidc={sidc(app6)} size={64} left={unit.number} right={unit.higher} />
      </div>
      <p className="text-ui leading-snug font-semibold text-ink">{bi(chosen)}</p>
      <dl className="text-ui">
        {[
          [t("Code APP-6"), <span className="font-mono tabular-nums">{shortApp6(app6)}</span>],
          [t("Tient un emplacement de la manœuvre"), chosen.manoeuvre ? t("oui") : t("non")],
          // Liste de missions d'un bataillon commandé au niveau régiment : seulement pour une fonction de manœuvre
          // (un bataillon médical ou de transmissions n'y est jamais commandé).
          ...(unit.side === "AMI" && chosen.manoeuvre ? [[t("Liste de missions utilisée au niveau régiment"), familyLabel(chosen.family)]] : []),
        ].map(([label, value], i) => (
          <div key={i} className="border-b border-line py-1.5 last:border-b-0">
            <dt className="text-meta leading-snug text-ink-muted">{label}</dt>
            <dd className="leading-snug text-ink">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );

  // Téléphone : aperçu sur une ligne (symbole, libellé, code), pour que les qualificatifs restent à l'écran.
  const compactPreview = chosen && (
    <div className="flex min-h-11 items-center gap-2.5">
      <UnitSymbol sidc={sidc(app6)} size={22} box={44} left={unit.number} right={unit.higher} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-ui font-semibold text-ink">{bi(chosen)}</span>
        <span className="block font-mono text-caps text-ink-muted tabular-nums">{shortApp6(app6)}</span>
      </span>
    </div>
  );
  const qualifierBlock = (
    <>
      <SectionLabel inset className="mb-1.5">
        {t("Qualificatif (secteur 2)")}
      </SectionLabel>
      <div role="radiogroup" aria-label={t("Qualificatif (secteur 2)")} className="flex flex-wrap gap-1.5">
        {[{ m2: "00", fr: "Aucun", en: "None" }, ...qualifiers].map((q) => {
          const active = m2 === q.m2;
          return (
            <button
              key={q.m2}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={!chosen}
              onClick={() => setM2(q.m2)}
              className={clsx(
                "inline-flex h-7 items-center rounded-md border px-2.5 text-meta transition-colors disabled:opacity-40",
                active ? "border-accent bg-accent-soft font-medium text-accent" : "border-line-strong bg-surface text-ink-soft hover:bg-hover",
              )}
            >
              {q.m2 === "00" ? t("Aucun") : bi(q)}
            </button>
          );
        })}
      </div>
    </>
  );
  const groupKeys = palette.groups.filter((g) => entries.some((e) => e.group === g.key));

  return (
    <Dialog
      title={t("Fonction APP-6")}
      subtitle={t("Symboles terrestres OTAN : le symbole choisi est celui de la carte et des exports.")}
      onClose={onClose}
      width={1040}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("Annuler")}
          </Button>
          <Button
            onClick={() => confirm()}
            blocked={!chosen}
            onBlocked={() => searchRef.current?.focus()}
            highlight={Boolean(chosen && (chosen.key !== initial?.key || app6 !== unit.app6))}
          >
            <Check size={14} /> {t("Choisir")}
          </Button>
        </>
      }
    >
      <div className="flex min-h-0 flex-col">
        <div className="border-b border-line px-4 py-3">
          <SearchField
            autoFocus
            inputRef={searchRef}
            value={query}
            onChange={setQuery}
            placeholder={t("Rechercher un symbole : infanterie, tank, 1211…")}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                const first = gridRef.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]') ??
                  gridRef.current?.querySelector<HTMLButtonElement>("[data-tile]");
                first?.focus();
              } else if (e.key === "Enter" && shown.length === 1) {
                e.preventDefault();
                confirm(shown[0]);
              }
            }}
          />
        </div>
        {!roomy && (
          // Téléphone : groupes en puces horizontales, aperçu compact et qualificatifs avant la grille, qui défile avec
          // la fenêtre (un seul défilement).
          <div className="space-y-2.5 border-b border-line px-4 py-2.5">
            <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-0.5" role="group" aria-label={t("Groupes de symboles")}>
              {[{ key: ALL, label: t("Tous les symboles") }, ...groupKeys.map((g) => ({ key: g.key, label: bi(g) }))].map((g) => {
                const active = !query.trim() && group === g.key;
                return (
                  <button
                    key={g.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      setGroup(g.key);
                      setQuery("");
                    }}
                    className={clsx(
                      "inline-flex h-7 shrink-0 items-center rounded-md border px-2.5 text-meta whitespace-nowrap transition-colors",
                      active ? "border-accent bg-accent-soft font-medium text-accent" : "border-line-strong bg-surface text-ink-soft hover:bg-hover",
                    )}
                  >
                    {g.label}
                  </button>
                );
              })}
            </div>
            {compactPreview}
            {qualifierBlock}
          </div>
        )}
        <div className="flex min-h-0">
          {roomy && (
            <nav aria-label={t("Groupes de symboles")} className="max-h-[46vh] w-52 shrink-0 space-y-0.5 overflow-y-auto border-r border-line px-2 py-2">
              {groupButton(ALL, t("Tous les symboles"), query.trim() ? found.length : entries.length, LayoutGrid)}
              {recentEntries.length > 0 && groupButton(RECENT, t("Récemment utilisés"), null, Clock)}
              {groupKeys.map((g) => groupButton(g.key, bi(g), query.trim() ? (counts.get(g.key) ?? 0) : entries.filter((e) => e.group === g.key).length))}
            </nav>
          )}
          <div className="min-w-0 flex-1">
            {shown.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
                <p className="text-ui text-ink-muted">{t("Aucun symbole ne correspond à « {query} »", { query: query.trim() })}</p>
                <Button size="sm" variant="secondary" onClick={() => setQuery("")}>
                  {t("Effacer la recherche")}
                </Button>
              </div>
            ) : (
              <div
                ref={gridRef}
                role="listbox"
                aria-label={t("Symboles")}
                onKeyDown={onGridKey}
                className={clsx(
                  "grid gap-2 p-3",
                  roomy ? "max-h-[46vh] grid-cols-[repeat(auto-fill,minmax(128px,1fr))] overflow-y-auto" : "grid-cols-[repeat(auto-fill,minmax(104px,1fr))]",
                )}
              >
                {shown.map((entry) => {
                  const selected = chosen?.key === entry.key;
                  return (
                    <button
                      key={entry.key}
                      type="button"
                      role="option"
                      data-tile=""
                      aria-selected={selected}
                      tabIndex={selected || (!chosen && entry === shown[0]) ? 0 : -1}
                      onClick={() => pick(entry)}
                      onDoubleClick={() => confirm(entry)}
                      title={bi(entry)}
                      className={clsx(
                        "flex min-h-[104px] flex-col items-center gap-1 rounded-lg border px-2 py-2 text-center transition-colors",
                        selected ? "border-accent bg-accent-soft" : "border-line bg-surface hover:bg-hover",
                      )}
                    >
                      <UnitSymbol sidc={sidc(entry.app6)} size={26} className="h-12" />
                      <span className={clsx("text-meta leading-snug break-words", selected ? "font-semibold text-accent" : "text-ink")}>
                        {bi(entry)}
                      </span>
                      <span className="font-mono text-caps text-ink-muted tabular-nums">{shortApp6(entry.app6)}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {wide && <aside className="max-h-[46vh] w-64 shrink-0 overflow-y-auto border-l border-line px-4 py-3">{preview}</aside>}
        </div>
        {!wide && roomy && chosen && <div className="border-t border-line px-4 py-3">{preview}</div>}
        {roomy && <div className="border-t border-line px-4 py-2.5">{qualifierBlock}</div>}
      </div>
    </Dialog>
  );
}
